import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import {
  AlertTriangle,
  CalendarClock,
  Package,
  Pencil,
  Trash2,
  Warehouse,
} from "lucide-react";
import type { PantryItem } from "../types";
import {
  formatAmount,
  formatAvailable,
  formatDate,
  isExpired,
  isExpiringSoon,
} from "../utils";

interface ItemRowProps {
  item: PantryItem;
  onEdit: (item: PantryItem) => void;
  onDelete: (item: PantryItem) => void;
}

export function ItemRow({ item, onEdit, onDelete }: ItemRowProps) {
  const { t } = useTranslation();
  const expired = isExpired(item.datumSpotreby);
  const expiringSoon = !expired && isExpiringSoon(item.datumSpotreby);
  const reserved = parseFloat(item.rezervovaneG) > 0;

  return (
    <div
      className={[
        "flex items-center gap-3 rounded-lg border p-3 transition-colors",
        expired ? "border-destructive/50 bg-destructive/5" : "",
        expiringSoon
          ? "border-yellow-400/60 bg-yellow-50/50 dark:bg-yellow-900/10"
          : "",
      ].join(" ")}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">
            {item.potravina.nazov}
          </span>
          <Badge variant="secondary" className="text-xs shrink-0">
            <Package className="h-3 w-3 mr-1" />
            {formatAmount(item)}
          </Badge>
          {reserved && (
            <Badge
              variant="outline"
              className="text-xs shrink-0 text-orange-600 border-orange-300"
            >
              {t("mealLibrary:spajza.reservedG", {
                amount: parseFloat(item.rezervovaneG).toFixed(0),
              })}
            </Badge>
          )}
          {item.datumSpotreby && (
            <Badge
              variant="outline"
              className={[
                "text-xs shrink-0 flex items-center gap-1",
                expired
                  ? "border-destructive text-destructive"
                  : expiringSoon
                    ? "border-yellow-500 text-yellow-700 dark:text-yellow-400"
                    : "text-muted-foreground",
              ].join(" ")}
            >
              {(expired || expiringSoon) && (
                <AlertTriangle className="h-3 w-3" />
              )}
              <CalendarClock className="h-3 w-3" />
              {formatDate(item.datumSpotreby)}
              {expired
                ? t("mealLibrary:spajza.expiredSuffix")
                : expiringSoon
                  ? t("mealLibrary:spajza.expiringSoonSuffix")
                  : ""}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatAvailable(item, t)}
          {item.ulozisko && (
            <span className="ml-2 inline-flex items-center gap-1">
              <Warehouse className="h-3 w-3" />
              {item.ulozisko.nazov}
            </span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={() => onEdit(item)}
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={() => onDelete(item)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
